/**
 * Engine wrapper: progressive quick→full load, streaming completions.
 * Default engineMode 'worker' keeps @mlc-ai/web-llm out of the main bundle
 * via WorkerEngineProxy. engineMode 'main' dynamic-imports WebLLM on the main thread.
 */

import { emit } from '../events.js';
import type {
  CoignConfig,
  CoignError,
  DownloadProgress,
  LoadPhase,
  ModelTier,
} from '../types.js';
import {
  getCustomModelRecord,
  getPreset,
  QUICK_FALLBACK_MODEL_ID,
  resolvePreset,
  resolvePresetName,
} from './presets.js';
import {
  createWorkerEngineProxy,
  takeOrCreateWorkerProxy,
  clearSharedWorkerProxy,
  preloadWorkerEngine,
  type WorkerEngineProxy,
} from './worker-engine-proxy.js';
import type { CustomRecordPayload } from './engine-rpc-types.js';

export { preloadWorkerEngine };

type WebLLMModule = typeof import('@mlc-ai/web-llm');
type EngineLike = {
  chat: {
    completions: {
      create: (req: any) => Promise<any>;
    };
  };
  unload: () => Promise<void> | void;
};

let webllmMod: WebLLMModule | null = null;
let engine: EngineLike | null = null;
let workerProxy: WorkerEngineProxy | null = null;
let isLoading = false;
let loadedModelId: string | null = null;
let currentTier: ModelTier = 'none';
let cancelRequested = false;
let loadPromise: Promise<void> | null = null;

export function getIsLoading(): boolean {
  return isLoading;
}

export function getTier(): ModelTier {
  return currentTier;
}

export function getLoadedModelId(): string | null {
  return loadedModelId;
}

async function loadWebLLM(): Promise<WebLLMModule> {
  if (webllmMod) return webllmMod;
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { __coignTestWebLLM?: WebLLMModule }).__coignTestWebLLM
  ) {
    webllmMod = (window as unknown as { __coignTestWebLLM: WebLLMModule }).__coignTestWebLLM;
    return webllmMod;
  }
  webllmMod = await import('@mlc-ai/web-llm');
  return webllmMod;
}

function mapStage(text: string): DownloadProgress['stage'] {
  const t = text.toLowerCase();
  if (t.includes('cache') || t.includes('check')) return 'checking-cache';
  if (t.includes('compile') || t.includes('build') || t.includes('wasm') || t.includes('shader'))
    return 'compiling';
  if (t.includes('downloa') || t.includes('fetch') || t.includes('weight')) return 'downloading';
  if (t.includes('finish') || t.includes('done') || t.includes('ready')) return 'ready';
  return 'downloading';
}

function makeError(kind: CoignError['kind'], message: string): CoignError {
  const e = new Error(message) as CoignError;
  e.kind = kind;
  return e;
}

function resolveModelId(presetOrId: string): string {
  const name = resolvePresetName(presetOrId);
  return resolvePreset(name);
}

export function resolveFullModelId(config: Partial<CoignConfig>): string {
  const source = config.preset || config.model || 'coign-lite';
  return resolveModelId(source);
}

export function resolveQuickModelId(config: Partial<CoignConfig>): string {
  const source = config.quickPreset || 'coign-quick';
  return resolveModelId(source);
}

function buildAppConfig(
  webllm: WebLLMModule,
  modelId: string,
  config: Partial<CoignConfig>,
  presetName?: string
): import('@mlc-ai/web-llm').AppConfig {
  const cacheBackend = config.cacheBackend ?? 'cache';

  if (config.modelUrl) {
    const base = config.modelUrl.replace(/\/$/, '');
    return {
      model_list: [
        {
          model: base,
          model_id: modelId,
          model_lib: base + '/' + modelId + '-webgpu.wasm',
          vram_required_MB: 0,
          low_resource_required: true,
        },
      ],
      cacheBackend,
    };
  }

  const custom =
    getCustomModelRecord(presetName ?? '') ?? getCustomModelRecord(modelId);

  if (custom) {
    return {
      model_list: [custom],
      cacheBackend,
    };
  }

  const prebuilt = webllm.prebuiltAppConfig.model_list.find((m) => m.model_id === modelId);
  if (prebuilt) {
    return {
      model_list: [prebuilt],
      cacheBackend,
    };
  }

  return {
    ...webllm.prebuiltAppConfig,
    cacheBackend,
  };
}

function workerLoadParams(
  modelId: string,
  config: Partial<CoignConfig>,
  presetName: string,
  onProgress?: (report: { progress: number; text: string }) => void,
  keepPrevious?: boolean
) {
  return {
    modelId,
    cacheBackend: config.cacheBackend ?? 'cache',
    modelUrl: config.modelUrl,
    customRecord: (getCustomModelRecord(presetName) ??
      getCustomModelRecord(modelId)) as CustomRecordPayload | undefined,
    onProgress,
    keepPrevious,
  };
}

export async function hasModelCached(
  tier: 'quick' | 'full' = 'quick',
  config?: Partial<CoignConfig>
): Promise<boolean> {
  const cfg = config ?? {};
  const modelId = tier === 'quick' ? resolveQuickModelId(cfg) : resolveFullModelId(cfg);
  const presetName =
    tier === 'quick'
      ? resolvePresetName(cfg.quickPreset ?? 'coign-quick')
      : resolvePresetName(cfg.preset || cfg.model || 'coign-lite');
  const mode = cfg.engineMode ?? 'worker';

  try {
    const useMainThreadWebllm =
      mode === 'main' ||
      (typeof window !== 'undefined' &&
        !!(window as unknown as { __coignTestWebLLM?: unknown }).__coignTestWebLLM) ||
      typeof Worker === 'undefined';

    if (useMainThreadWebllm) {
      const webllm = await loadWebLLM();
      const appConfig = buildAppConfig(webllm, modelId, cfg, presetName);
      return await webllm.hasModelInCache(modelId, appConfig);
    }

    const proxy = workerProxy ?? createWorkerEngineProxy();
    const owned = !workerProxy;
    try {
      return await proxy.hasModelInCache(workerLoadParams(modelId, cfg, presetName));
    } finally {
      if (owned) proxy.terminate();
    }
  } catch {
    return false;
  }
}

async function createMainEngine(
  webllm: WebLLMModule,
  modelId: string,
  config: Partial<CoignConfig>,
  phase: LoadPhase,
  fromCache: boolean,
  presetName: string
): Promise<EngineLike> {
  const appConfig = buildAppConfig(webllm, modelId, config, presetName);
  return (await webllm.CreateMLCEngine(modelId, {
    appConfig,
    initProgressCallback: (report: { progress: number; text: string }) => {
      if (cancelRequested) return;
      const progress: DownloadProgress = {
        stage: mapStage(report.text),
        progress: report.progress,
        text: report.text,
        phase,
        fromCache,
      };
      emit('load', { progress: report.progress, text: report.text, phase });
      emit('downloadProgress', progress);
      if (config.onDownloadProgress) config.onDownloadProgress(progress);
    },
  })) as unknown as EngineLike;
}

async function createWorkerEngine(
  modelId: string,
  config: Partial<CoignConfig>,
  phase: LoadPhase,
  presetName: string,
  keepPrevious: boolean
): Promise<{ engine: EngineLike; fromCache: boolean }> {
  const proxy = workerProxy ?? takeOrCreateWorkerProxy();
  workerProxy = proxy;

  const { fromCache } = await proxy.init(
    workerLoadParams(
      modelId,
      config,
      presetName,
      (report) => {
        if (cancelRequested) return;
        const progress: DownloadProgress = {
          stage: mapStage(report.text),
          progress: report.progress,
          text: report.text,
          phase,
          fromCache: false,
        };
        emit('load', { progress: report.progress, text: report.text, phase });
        emit('downloadProgress', progress);
        if (config.onDownloadProgress) config.onDownloadProgress(progress);
      },
      keepPrevious
    )
  );

  return { engine: proxy, fromCache };
}

async function unloadCurrent(): Promise<void> {
  if (engine) {
    try {
      await engine.unload();
    } catch {
      /* ignore */
    }
    engine = null;
  }
  if (workerProxy) {
    try {
      workerProxy.terminate();
    } catch {
      /* ignore */
    }
    workerProxy = null;
  }
  clearSharedWorkerProxy();
  loadedModelId = null;
}

/**
 * Load a model for the given phase. Idempotent if already loaded with same model.
 */
export async function loadModel(
  phase: LoadPhase,
  config: Partial<CoignConfig>
): Promise<void> {
  const presetName =
    phase === 'quick'
      ? resolvePresetName(config.quickPreset || 'coign-quick')
      : resolvePresetName(config.preset || config.model || 'coign-lite');
  let modelId = phase === 'quick' ? resolveQuickModelId(config) : resolveFullModelId(config);
  const mode = config.engineMode ?? 'worker';
  const useTestMock =
    typeof window !== 'undefined' &&
    !!(window as unknown as { __coignTestWebLLM?: unknown }).__coignTestWebLLM;
  const useMain = mode === 'main' || useTestMock;

  if (engine && loadedModelId === modelId) {
    currentTier = phase === 'quick' ? 'quick' : 'full';
    return;
  }

  if (loadPromise) {
    await loadPromise;
    if (engine && loadedModelId === modelId) {
      currentTier = phase === 'quick' ? 'quick' : 'full';
      return;
    }
  }

  if (isLoading) {
    throw makeError('config', 'Model is already loading');
  }

  isLoading = true;
  cancelRequested = false;

  loadPromise = (async () => {
    const previousTier = currentTier;
    try {
      emit('downloadStart', { phase });

      const swapping = !!(engine && loadedModelId && loadedModelId !== modelId);
      const keepQuickDuringSwap =
        !useMain && swapping && previousTier === 'quick' && phase === 'full';

      // Worker path: in-place swap keeps previous engine serving (keepPrevious).
      // Main path: must unload first — tier drops to none until the new model is ready.
      if (swapping && useMain) {
        await unloadCurrent();
        currentTier = 'none';
      }

      let fromCache = false;

      try {
        if (useMain) {
          const webllm = await loadWebLLM();
          fromCache = await webllm.hasModelInCache(
            modelId,
            buildAppConfig(webllm, modelId, config, presetName)
          );
          engine = await createMainEngine(
            webllm,
            modelId,
            config,
            phase,
            fromCache,
            presetName
          );
        } else {
          const result = await createWorkerEngine(
            modelId,
            config,
            phase,
            presetName,
            keepQuickDuringSwap
          );
          engine = result.engine;
          fromCache = result.fromCache;
        }
      } catch (err) {
        if (phase === 'quick' && modelId !== QUICK_FALLBACK_MODEL_ID) {
          console.warn(
            `[Coign] Quick model ${modelId} failed, falling back to ${QUICK_FALLBACK_MODEL_ID}`,
            err
          );
          modelId = QUICK_FALLBACK_MODEL_ID;
          if (useMain) {
            const webllm = await loadWebLLM();
            engine = await createMainEngine(
              webllm,
              modelId,
              config,
              phase,
              false,
              'coign-tiny'
            );
          } else {
            const result = await createWorkerEngine(
              modelId,
              config,
              phase,
              'coign-tiny',
              false
            );
            engine = result.engine;
          }
        } else {
          throw err;
        }
      }

      if (cancelRequested) {
        await unloadCurrent();
        currentTier = 'none';
        throw makeError('init', 'Model download was cancelled by the user.');
      }

      loadedModelId = modelId;
      currentTier = phase === 'quick' ? 'quick' : 'full';

      // Re-emit a progress tick with accurate fromCache for overlay copy
      emit('downloadProgress', {
        stage: 'ready',
        progress: 1,
        text: fromCache ? 'Loaded from cache' : 'Ready',
        phase,
        fromCache,
      });

      emit('downloadComplete', { phase });
      emit('modelReady', { tier: currentTier, phase });
      emit('ready', { tier: currentTier, phase });

      if (config.onDownloadComplete) {
        try {
          config.onDownloadComplete({ phase });
        } catch (e) {
          console.error('[Coign] onDownloadComplete callback error:', e);
        }
      }
      if (config.onModelReady) {
        try {
          config.onModelReady({ tier: currentTier, phase });
        } catch (e) {
          console.error('[Coign] onModelReady callback error:', e);
        }
      }
    } catch (err) {
      const coignErr =
        err instanceof Error && (err as CoignError).kind
          ? (err as CoignError)
          : makeError('init', err instanceof Error ? err.message : String(err));

      emit('downloadError', coignErr);
      emit('error', coignErr);
      if (config.onDownloadError) {
        try {
          config.onDownloadError(coignErr);
        } catch (e) {
          console.error('[Coign] onDownloadError callback error:', e);
        }
      }
      throw coignErr;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/** @deprecated Use loadModel — kept for tests that call initEngine directly. */
export async function initEngine(config: CoignConfig): Promise<void> {
  const useQuick = config.quick !== false;
  if (useQuick) {
    await loadModel('quick', config);
  } else {
    await loadModel('full', config);
  }
}

export function cancelEngineInit(): void {
  cancelRequested = true;
}

export interface StreamOptions {
  onToken?: (token: string) => void;
  stream?: boolean;
  phase?: LoadPhase;
}

export async function chatCompletion(
  messages: import('@mlc-ai/web-llm').ChatCompletionMessageParam[],
  options?: Partial<import('@mlc-ai/web-llm').ChatCompletionRequest> & StreamOptions
): Promise<string> {
  if (!engine) {
    throw makeError('model', 'Engine not initialized. Call warmUp() or ask() first.');
  }

  const stream = options?.stream !== false;
  const phase = options?.phase ?? (currentTier === 'full' ? 'full' : 'quick');
  const { onToken, phase: _p, stream: _s, ...rest } = options ?? {};

  try {
    if (stream) {
      const reply = await engine.chat.completions.create({
        messages,
        stream: true,
        ...rest,
      });
      let full = '';
      for await (const chunk of reply as AsyncIterable<any>) {
        const delta = chunk?.choices?.[0]?.delta?.content ?? '';
        if (delta) {
          full += delta;
          emit('token', { token: delta, phase });
          onToken?.(delta);
        }
      }
      return full;
    }

    const reply = await engine.chat.completions.create({
      messages,
      stream: false,
      ...rest,
    });
    const completion = reply as import('@mlc-ai/web-llm').ChatCompletion;
    const choice = completion.choices[0];
    if (!choice) {
      throw makeError('model', 'No completion returned from model.');
    }
    return choice.message.content ?? '';
  } catch (err) {
    throw makeError('model', err instanceof Error ? err.message : String(err));
  }
}

export function getEngine(): EngineLike | null {
  return engine;
}

export async function destroyEngine(): Promise<void> {
  cancelRequested = true;
  await unloadCurrent();
  currentTier = 'none';
  isLoading = false;
  loadPromise = null;
  webllmMod = null;
}

export function getPresetDownloadMb(presetOrId: string): number | undefined {
  const name = resolvePresetName(presetOrId);
  return getPreset(name)?.downloadMb;
}
